import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { useRef, useEffect, useState, forwardRef } from 'react';
import { mergeProps, useButton, useTextField, useHover, usePress, useFocusRing } from 'react-aria';

import { mergeRefs } from '../../../lib/mergeRefs';
import { ToastManager } from '../../../ToastManager.jsx';

import { variablesStore } from '../../../store/logic/VariablesStore.ts';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useVariable } from '../../../store/logic/useVariables.ts';
import { useApi } from '../../../store/SelectedHomeyStore.hooks.ts';

import { su } from '../../../theme/functions/su';
import { input } from '../../../theme/Global';
import { glyph } from '../../../theme/glyph';
import { motion } from '../../../theme/motion';
import { theme } from '../../../theme/theme';

import { Glyph } from '../../../components/common/Glyph';
import { RoundIconWrapper } from '../../../components/common/RoundIconWrapper';
import { promptMessage } from '../../../components/overlay/DialogPrompt';

import { BooleanSelect } from './BooleanSelect';

const iconMap = {
  boolean: {
    source: glyph.logic.boolean,
    color: theme.color.logic_boolean,
  },
  number: {
    source: glyph.logic.number,
    color: theme.color.logic_number,
  },
  string: {
    source: glyph.logic.string,
    color: theme.color.logic_string,
  },
};

export function VariablesListItem({ variableId }) {
  const { i18n } = useI18n();
  const { api } = useApi();
  const { variable } = useVariable({ variableId });

  const nameRef = useRef();
  const valueRef = useRef();
  const buttonRef = useRef();
  const [isEditName, setIsEditName] = useState(false);
  const [isEditValue, setIsEditValue] = useState(false);
  const [nameSubmissionVersion, setNameSubmissionVersion] = useState(0);
  const [valueSubmissionVersion, setValueSubmissionVersion] = useState(0);

  const hover = useHover({});
  const button = useButton(
    {
      onPress: () => {
        promptMessage({
          type: 'danger',
          message: i18n.messageFormatter('logic.removeVariablePrompt', {
            name: variable.name,
          }),
        })
          .then(() => {
            api.logic.deleteVariable({
              id: variable?.id,
            });
          })
          .catch(ToastManager.handleError);
      },
    },
    buttonRef,
  );

  const focusRing = useFocusRing({});

  if (variable == null) return null;

  function onRequestSaveName({ name }) {
    variablesStore
      .updateVariableOptimistically({
        id: variable.id,
        variable: {
          name,
        },
      })
      .catch(ToastManager.handleError);
  }

  function onRequestSaveValue({ value }) {
    variablesStore
      .updateVariableOptimistically({
        id: variable.id,
        variable: {
          value: inputType === 'number' ? Number(value) : value,
        },
      })
      .catch(ToastManager.handleError);
  }

  const inputType = (() => {
    switch (variable.type) {
      case 'string':
        return 'text';
      case 'number':
        return 'number';
      case 'boolean':
        return 'select';
      default:
        return 'text';
    }
  })();

  const isDeleteIconVisible = hover.isHovered || focusRing.isFocusVisible;

  function focusInput(inputRef) {
    inputRef.current?.focus();
  }

  return (
    <sc.ListItem>
      <sc.VariableButton
        {...mergeProps(hover.hoverProps, focusRing.focusProps, button.buttonProps)}
        ref={buttonRef}
      >
        <RoundIconWrapper
          size={theme.icon.size_medium}
          color={isDeleteIconVisible ? theme.color.danger : iconMap[variable.type].color}
        >
          <Glyph
            size={isDeleteIconVisible ? theme.icon.size_tiny : theme.icon.size_small}
            source={isDeleteIconVisible ? glyph.core.cross : iconMap[variable.type].source}
            color={theme.color.icon_white}
          />
        </RoundIconWrapper>
      </sc.VariableButton>

      <TextField
        ref={nameRef}
        type="text"
        align="start"
        aria-label="Name"
        placeholder={i18n.messageFormatter(`logic.namePlaceholder`)}
        value={variable.name}
        isReadOnly={isEditName === false}
        submissionVersion={nameSubmissionVersion}
        onFocus={() => {
          setIsEditName(true);
        }}
        onPress={() => {
          setIsEditName(true);
          focusInput(nameRef);
        }}
        onBlur={(event) => {
          if (isEditName === false) {
            return;
          }

          const value = event.target.value;
          setIsEditName(false);

          onRequestSaveName({
            name: value,
          });
        }}
        onKeyDown={(event) => {
          const key = event.key;
          switch (key) {
            case 'Tab':
              if (event.shiftKey === true) {
                break;
              }

              event.preventDefault();
              setIsEditValue(true);
              focusInput(valueRef);
              break;
            default:
              break;
          }
        }}
        onKeyUp={(event) => {
          const key = event.key;
          switch (key) {
            case 'Enter': {
              if (isEditName === false) {
                setIsEditName(true);
                break;
              }

              const value = event.target.value;

              setIsEditName(false);
              setNameSubmissionVersion((currentVersion) => {
                return currentVersion + 1;
              });
              onRequestSaveName({
                name: value,
              });

              break;
            }
            case 'Escape':
              setIsEditName(false);
              break;
            default:
              break;
          }
        }}
      />

      {inputType === 'select' ? (
        <BooleanSelect
          inputRef={valueRef}
          align="right"
          aria-label="Value"
          value={variable.value}
          onChange={(value) => {
            onRequestSaveValue({
              inputType: inputType,
              value: value,
            });
          }}
        />
      ) : (
        <TextField
          ref={valueRef}
          type={inputType}
          align="end"
          aria-label="Value"
          placeholder={i18n.messageFormatter(`logic.${variable.type}Placeholder`)}
          isReadOnly={isEditValue === false}
          value={variable.value}
          submissionVersion={valueSubmissionVersion}
          onFocus={() => {
            setIsEditValue(true);
          }}
          onPress={() => {
            setIsEditValue(true);
            focusInput(valueRef);
          }}
          onBlur={(event) => {
            if (isEditValue === false) {
              return;
            }

            const value = event.target.value;
            setIsEditValue(false);

            onRequestSaveValue({
              inputType: inputType,
              value: value,
            });
          }}
          onKeyDown={(event) => {
            const key = event.key;
            switch (key) {
              case 'Tab':
                if (event.shiftKey === false) {
                  break;
                }

                event.preventDefault();
                setIsEditName(true);
                focusInput(nameRef);
                break;
              default:
                break;
            }
          }}
          onKeyUp={(event) => {
            const key = event.key;

            switch (key) {
              case 'Enter': {
                if (isEditValue === false) {
                  setIsEditValue(true);
                  break;
                }

                const value = event.target.value;

                setIsEditValue(false);
                setValueSubmissionVersion((currentVersion) => {
                  return currentVersion + 1;
                });
                onRequestSaveValue({
                  inputType: inputType,
                  value: value,
                });
                break;
              }
              case 'Escape':
                setIsEditValue(false);
                break;
              default:
                break;
            }
          }}
        />
      )}
    </sc.ListItem>
  );
}

const TextField = forwardRef(function (props, forwardedRef) {
  const ref = useRef();
  const value = props.value == null ? '' : String(props.value);
  const [draftValue, setDraftValue] = useState(value);
  const textField = useTextField(
    {
      ...props,
      value: draftValue,
      onChange: setDraftValue,
    },
    ref,
  );
  const press = usePress({
    onPress: props.onPress,
  });

  useEffect(() => {
    if (props.isReadOnly === true) {
      setDraftValue(value);
    }
  }, [value, props.isReadOnly]);

  return (
    <sc.Input
      {...mergeProps(textField.inputProps, props.isReadOnly ? press.pressProps : {})}
      aria-label={props['aria-label']}
      title={value}
      align={props.align}
      data-submission-version={props.submissionVersion}
      isReadOnly={props.isReadOnly}
      submissionVersion={props.submissionVersion}
      justifySelf={props.justifySelf}
      ref={mergeRefs([ref, forwardedRef])}
      step={props.type === 'number' ? 'any' : undefined}
    />
  );
});

const VariableButton = styled.button`
  //cursor: pointer;
`;

const ListItem = styled.li`
  display: grid;
  grid-template-columns: auto 1fr 1fr;
  grid-auto-rows: ${su(5)};
  grid-gap: ${su(1)};

  align-items: center;
  outline: 0;
  padding: 0 ${su(2)};
  line-height: 20px;

  &:hover {
    background-color: ${theme.color.background_hover};
  }
`;

// Alternating names restarts the animation on consecutive submissions.
const submissionPulseOdd = keyframes`
  0%, 100% {
    outline: 0 solid ${theme.input.border_focus};
  }

  45% {
    outline: 3px solid ${theme.input.border_focus};
  }
`;

const submissionPulseEven = keyframes`
  0%, 100% {
    outline: 0 solid ${theme.input.border_focus};
  }

  46% {
    outline: 3px solid ${theme.input.border_focus};
  }
`;

const Input = styled.input`
  ${input};
  min-width: 0;
  background-color: ${theme.input.background};
  border: 1px solid ${theme.input.border};
  outline: 0;
  border-radius: ${theme.borderRadius.default};
  line-height: 30px;

  text-align: ${(props) => props.align};
  padding: 0 5px;
  margin-${(props) => props.align}: -6px;

  &:hover {
    border-color: ${theme.input.border};
    background: ${theme.color.component};
  }

  ${(props) =>
    props.submissionVersion > 0 &&
    css`
      animation: ${props.submissionVersion % 2 === 0 ? submissionPulseEven : submissionPulseOdd}
        ${motion.duration.normal} ${motion.curve.ease_out};
      outline-offset: 1px;
    `};

  ${(props) =>
    props.isReadOnly &&
    css`
      background-color: transparent;
      border-color: transparent;
      caret-color: transparent;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      cursor: pointer;

      &:hover {
        background-color: ${theme.color.background_hover};
      }
    `};
`;

const sc = {
  ListItem,
  VariableButton,
  Input,
};
